import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { StepProps } from "../../types/intake";
import { useI18n } from "../../i18n/I18nProvider";

export function WelcomeStep({ onNext }: StepProps) {
  const { t } = useI18n();

  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] text-center space-y-16 relative overflow-hidden">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.5, ease: [0.25, 0.1, 0.25, 1] }}
        className="relative"
      >
        <div className="w-24 h-24 rounded-full animated-gradient-circle opacity-90 organic-shadow" />
      </motion.div>

      <div className="space-y-8 max-w-2xl mx-auto z-10">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-5xl sm:text-6xl font-normal text-[#2D2A35] tracking-tight leading-tight"
        >
          {t("welcome.title.prefix")}{" "}
          <span className="font-medium intention-gradient">
            {t("welcome.title.highlight")}
          </span>
          {t("welcome.title.suffix")}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-[clamp(1.1rem,3.5vw,1.25rem)] sm:text-2xl text-[#57534E] font-light space-y-3"
        >
          <p>{t("welcome.body.line1")}</p>
          <p>{t("welcome.body.line2")}</p>
          <p>
            {t("welcome.body.line3a")}
            <br className="sm:hidden" />
            {t("welcome.body.line3b")}
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        className="pt-12 flex justify-center"
      >
        <motion.button
          onClick={onNext}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          className="w-20 h-20 rounded-full gradient-button tactile-texture text-white organic-shadow hover:shadow-xl transition-all duration-500 flex items-center justify-center"
          aria-label={t("welcome.cta")}
        >
          <ArrowRight className="w-7 h-7 stroke-[1.5] relative z-10" />
        </motion.button>
      </motion.div>
    </div>
  );
}